/**
 * FamilyCheckInDialog — Ted #32c / epic E5.1 (#1160), from Ted's 09-17 note
 * "Multi-Dog Check-In — Select Which Family Dogs to Check In":
 *
 * > "When I selected Check In for Alpin, the system automatically checked in
 * > both family dogs. It's great that the system recognizes dogs from the same
 * > family, but it should not automatically check them all in. Staff should be
 * > shown all linked dogs and have the option to select one dog, multiple dogs,
 * > or all dogs before confirming. There may be times, for whatever reason,
 * > when we only want to check in one dog."
 *
 * ONE picker for every check-in surface — Bookings, Front Desk, the dashboard
 * — extracted from the Bookings page's "Check in the whole family?" prompt
 * (Ted #26 / #987) so the three surfaces cannot word it, default it or batch
 * it differently (Principle 1: one truth).
 *
 * THE DEFAULT IS THE WHOLE POINT. Tapping Check In on a dog opens this with
 * ONLY THAT DOG going in and every sibling UNTICKED ("Tick any that are here
 * too"): nothing happens to a sibling unless the desk ticks it. The only
 * entry that starts with everyone ticked is the explicit "Check in all (N)"
 * button — the desk asked for all of them, and can still untick any dog that
 * is not here yet.
 *
 * Every exit is explicit and none of them checks a dog in: focus lands on
 * "Don't check anyone in" (a stray Enter on a mis-tapped row is a no-op) and
 * Escape closes it. The dialog posts nothing itself — it hands the parent the
 * ids the desk chose, and the parent runs its own check-in path (so "Before
 * you check in", the transport confirmation and the batch rules all still
 * apply, unchanged).
 *
 * Label guard (#1103): every input has `id` and `name`.
 */
import { useEffect, useId, useRef, useState } from 'react';

/** One dog's visit in the family, as the picker shows it. */
export interface FamilyCheckInMember {
  readonly bookingId: string;
  /** "Biscuit" — the dogs on the visit, joined. Never a raw id. */
  readonly dogLabel: string;
  /** How many dogs the visit carries — one booking can hold two. Default 1. */
  readonly dogCount?: number;
  /** "Daycare · 07:30–17:00" — what the desk needs to recognise the visit. */
  readonly summary: string;
  /** The reservation's note, when it has one ("Half day — back at noon"). */
  readonly note?: string | null;
}

export interface FamilyCheckInDialogProps {
  /**
   * The dog whose Check In was tapped — always going in. `null` for the
   * "Check in all (N)" entry, where no single dog was tapped.
   */
  readonly anchor: FamilyCheckInMember | null;
  /** Everyone else in the family who is still to arrive (never the anchor). */
  readonly siblings: readonly FamilyCheckInMember[];
  /** "Wren Halloway". */
  readonly ownerLabel: string;
  /**
   * Which dogs start ticked. `'anchor-only'` (a row's Check In): none of the
   * siblings. `'all'` (the explicit "Check in all"): every one.
   */
  readonly startTicked: 'anchor-only' | 'all';
  /**
   * The list is built from a FILTERED schedule on some surfaces, so it cannot
   * speak for the whole day; this says what is narrowing it. Advisory.
   */
  readonly scopeNote?: string | null;
  readonly onClose: () => void;
  /** The booking ids the desk chose — the anchor first when there is one. */
  readonly onConfirm: (bookingIds: readonly string[]) => void;
}

/** "1 dog" / "3 dogs" — counted in dogs, never in visit rows. */
function dogCountLabel(n: number): string {
  return `${n} ${n === 1 ? 'dog' : 'dogs'}`;
}

/** The dogs a visit carries (a booking can hold two). */
function dogsIn(members: readonly FamilyCheckInMember[]): number {
  return members.reduce((sum, m) => sum + Math.max(1, m.dogCount ?? 1), 0);
}

export function FamilyCheckInDialog({
  anchor,
  siblings,
  ownerLabel,
  startTicked,
  scopeNote = null,
  onClose,
  onConfirm,
}: FamilyCheckInDialogProps): JSX.Element {
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(
    () => new Set(startTicked === 'all' ? siblings.map((s) => s.bookingId) : []),
  );

  // Focus lands on the no-op, never on an action: a modal that opens under a
  // mistaken tap must have an exit that does nothing.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const chosen = siblings.filter((s) => ticked.has(s.bookingId));
  const goingIn = dogsIn(chosen) + (anchor ? dogsIn([anchor]) : 0);
  const allTicked = chosen.length === siblings.length;
  const ids = [...(anchor ? [anchor.bookingId] : []), ...chosen.map((s) => s.bookingId)];
  const familyDogs = dogsIn(siblings) + (anchor ? dogsIn([anchor]) : 0);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="sibling-check-in-dialog"
      className="ferro-blocking-dialog__overlay"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onClose();
      }}
    >
      <div className="ferro-blocking-dialog__panel">
        <h2 id={titleId}>Check in the whole family?</h2>
        {anchor ? (
          <p>
            {anchor.dogLabel} is checking in. Also on today&apos;s list for {ownerLabel}:{' '}
            {dogCountLabel(dogsIn(siblings))}. Tick any that are here too.
          </p>
        ) : (
          <p>
            {ownerLabel} has {dogCountLabel(familyDogs)} on today&apos;s list. Untick any that are
            not here yet.
          </p>
        )}
        {scopeNote ? (
          <p className="ferro-sibling-prompt__scope" data-testid="sibling-check-in-scope">
            {scopeNote}
          </p>
        ) : null}
        <ul className="ferro-sibling-prompt__list" data-testid="sibling-check-in-list">
          {siblings.map((sib) => (
            <li key={sib.bookingId} className="ferro-sibling-prompt__item">
              <label className="ferro-sibling-prompt__label">
                <input
                  type="checkbox"
                  id={`family-check-in-${sib.bookingId}`}
                  name={`family-check-in-${sib.bookingId}`}
                  className="ferro-sibling-prompt__box"
                  checked={ticked.has(sib.bookingId)}
                  data-testid={`sibling-check-in-toggle-${sib.bookingId}`}
                  onChange={(e) =>
                    setTicked((curr) => {
                      const next = new Set(curr);
                      if (e.target.checked) next.add(sib.bookingId);
                      else next.delete(sib.bookingId);
                      return next;
                    })
                  }
                />
                <span className="ferro-sibling-prompt__dog">{sib.dogLabel}</span>
                <span className="ferro-sibling-prompt__meta">{sib.summary}</span>
              </label>
              {sib.note ? <p className="ferro-sibling-prompt__note">{sib.note}</p> : null}
            </li>
          ))}
        </ul>
        <div className="ferro-blocking-dialog__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            ref={cancelRef}
            onClick={onClose}
            data-testid="sibling-check-in-cancel"
          >
            Don&apos;t check anyone in
          </button>
          {anchor ? (
            <button
              type="button"
              // The one filled primary is whichever choice matches the ticks:
              // nothing ticked ⇒ this dog only; something ticked ⇒ the family.
              className={`ferro-btn${chosen.length === 0 ? ' ferro-btn--primary' : ' ferro-btn--ghost'}`}
              onClick={() => onConfirm([anchor.bookingId])}
              data-testid="sibling-check-in-single"
            >
              Check in {anchor.dogLabel} only
            </button>
          ) : null}
          {chosen.length > 0 || !anchor ? (
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              disabled={goingIn === 0}
              onClick={() => onConfirm(ids)}
              data-testid="sibling-check-in-all"
            >
              {goingIn === 0
                ? 'Tick a dog to check in'
                : allTicked
                  ? `Check in all ${dogCountLabel(goingIn)}`
                  : `Check in ${dogCountLabel(goingIn)}`}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
